/**
 * Type definitions for search results UI components
 */

import { SearchResults as NormalizedSearchResults } from "../search-core/types/SearchResults";
import { ProductResult } from "../search-core/types/ProductResult";
import { CategoryResult } from "../search-core/types/CategoryResult";
import { SearchHistoryItem } from "../search-core/history";

/**
 * Product hit from Meilisearch with valid feed object
 * This represents a filtered hit that passed server-side validation
 */
export interface ProductHit {
	/** Original hit ID */
	id?: string | number;
	/** Feed object containing product data */
	feed: {
		/** Product image URL */
		picture?: string;
		/** Product name */
		name?: string;
		/** Display price */
		price?: string;
		/** Product URL */
		url?: string;
		/** Product availability */
		available?: boolean;
		/** Old price for discount display */
		oldPrice?: number | null;
		/** Any other feed fields */
		[key: string]: unknown;
	};
	/** Any other hit fields */
	[key: string]: unknown;
}

/**
 * Check if a hit is a valid ProductHit
 */
export function isValidProductHit(hit: unknown): hit is ProductHit {
	if (!hit || typeof hit !== "object") return false;

	const hitObj = hit as Record<string, unknown>;
	if (!hitObj.feed || typeof hitObj.feed !== "object") return false;

	return true;
}

/**
 * Convert unknown hit to ProductHit if possible
 */
export function toProductHit(hit: unknown): ProductHit | null {
	if (isValidProductHit(hit)) {
		return hit;
	}
	return null;
}

/**
 * ProductCard component props
 */
export interface ProductCardProps {
	/** Product data (legacy ProductHit or normalized ProductResult) */
	product: ProductHit | ProductResult;
	/** Optional CSS class */
	className?: string;
	/** Callback when action button is clicked */
	onActionClick?: (product: ProductHit | ProductResult) => void;

	hasMore?: boolean;
}

/**
 * ProductsGrid component props
 */
export interface ProductsGridProps {
	/** Array of product data to display (ProductHit, ProductResult, or unknown) */
	products: unknown[];
	/** Whether grid is loading (show skeletons) */
	isLoading?: boolean;
	/** Optional CSS class */
	className?: string;
	/** Number of skeleton items to show during loading */
	skeletonCount?: number;

	hasMore?: boolean;
}

/**
 * CategoriesBlock component props
 */
export interface CategoriesBlockProps {
	/** Array of category results */
	categories?: CategoryResult[];
	/** Optional CSS class */
	className?: string;
	/** Callback when category is selected */
	onCategorySelect?: (category: CategoryResult) => void;
}

/**
 * SuggestionsBlock component props (stub)
 */
export interface SuggestionsBlockProps {
	/** Array of suggestion queries (stub data) */
	suggestions?: string[];
	/** Optional CSS class */
	className?: string;
	/** Callback when suggestion is clicked */
	onSuggestionClick?: (query: string) => void;
}

/**
 * Quick query chip props
 */
export interface QuickQueryChip {
	/** Query text */
	query: string;
	/** Optional icon */
	icon?: string;
}

/**
 * SearchResults container component props
 */
export interface SearchResultsProps {
	/** Array of raw hits from useLiveSearch - legacy, temporary */
	hits: unknown[];
	/** Normalized search results for UI consumption */
	normalizedResults?: NormalizedSearchResults | null;
	/** Search query */
	query: string;
	/** Search history items (filtered by current query) */
	history?: SearchHistoryItem[];
	/** Callback when a history item is selected */
	onHistorySelect?: (query: string) => void;
	/** Callback when a suggestion is clicked */
	onSuggestionClick?: (query: string) => void;
	/** Search suggestions (strings only) */
	suggestions?: string[];
	/** Loading state */
	isLoading: boolean;
	/** Error state */
	hasError: boolean;
	/** Empty results state */
	isEmpty: boolean;
	/** Error message */
	error?: string | null;
	/** Callback to retry search */
	onRetry?: () => void;
	/** Callback to load more results (pagination) */
	loadMore?: () => void;
	/** Whether there are more results to load */
	hasMore?: boolean;
	/** Optional CSS class */
	className?: string;
}

/**
 * UI state for search results
 */
export type SearchResultsState = "idle" | "loading" | "success" | "empty" | "error";
